import React from "react";
import { styled } from "@mui/system";
import { ButtonProps } from "@mui/material";

interface WrapperProps {
  width: string;
  height: string;
  borderRadius?: string;
  background: string;
  boxShadow: string;
  focusBackground: string;
  hoverBackground: string;
  color: string;
  activeColor: string;
  disabledBackground: string;
  disabledColor: string;
}

const Wrapper = styled("button")<WrapperProps>(
  ({
    width,
    height,
    borderRadius,
    background,
    boxShadow,
    focusBackground,
    hoverBackground,
    color,
    activeColor,
    disabledBackground,
    disabledColor,
  }) => ({
    width: width,
    height: height,
    display: "inline-flex",
    padding: "8px 12px",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    borderRadius: borderRadius ?? "4px",
    background: background,
    color: color,
    outline: "none",
    border: "none",
    cursor: "pointer",
    boxShadow: boxShadow && `0px 4px 4px 0px ${boxShadow}`,

    "&:hover": {
      background: hoverBackground,
    },

    "&:active": {
      background: focusBackground,
      color: activeColor,
    },

    "&:disabled": {
      background: disabledBackground,
      color: disabledColor,
      cursor: "not-allowed",
    },
  }),
);

interface IconWrapperProps {
  hide: boolean;
}
const IconWrapper = styled("div")<IconWrapperProps>(({ hide }) => ({
  display: hide ? "none" : "flex",
  paddingRight: "0.231px",
  alignItems: "center",
}));

interface ITextProps {
  fontWeight?: number;
}
const Text = styled("div")<ITextProps>(({ fontWeight }) => ({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: fontWeight ?? 600,
  lineHeight: "19px",
}));

interface ContainedButtonProps extends Omit<ButtonProps, "color"> {
  width?: string;
  height?: string;
  borderRadius?: string;
  color: "salmon" | "navy" | "neutral" | "dark";
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  children: React.ReactNode;
  disabled?: boolean;
  fontWeight?: number;
  sx?: any;
  onClick?: () => void;
}

/**
 * A button component with contained style.
 * @param {string} props.width - The width of the button.
 * @param {string} props.height - The height of the button.
 * @param {string} [props.borderRadius] - The border radius of the button (optional).
 * @param {"salmon" | "navy"} props.color - The color scheme of the button.
 * @param {JSX.Element} [props.icon] - The icon to display inside the button (optional).
 * @param {React.ReactNode} props.children - The content inside the button.
 * @param {boolean} [props.disabled] - Whether the button is disabled (optional).
 */
const ContainedButton = (props: ContainedButtonProps) => {
  const {
    width = "100%",
    height = "100%",
    borderRadius,
    color,
    startIcon,
    endIcon,
    children,
    disabled,
    fontWeight = 600,
    sx,
    onClick,
  } = props;
  const startIconNonExistent = !Boolean(startIcon);
  const endIconNonExistent = !Boolean(endIcon);

  // background color
  let background = "";
  let boxShadow = "";
  let hoverBackground = "";
  let activeBackground = "";
  let disabledBackground = "#D1D4D6";

  // text color
  let textColor = "#FFFFFF";
  let textActiveColor = "";
  let textDisabledColor = "#BDC2C5";

  switch (color) {
    case "salmon":
      background = "#FC4C4C";
      boxShadow = "";
      hoverBackground = "#FEDBDB";
      textActiveColor = "#FC4C4C";
      break;
    case "navy":
      background = "#002035";
      boxShadow = "rgba(42, 95, 129, 0.30)";
      hoverBackground = "#4E7086";
      activeBackground = "#E8F6FF";
      textActiveColor = "#002035";
      break;
    case "neutral":
      background = "#FFF";
      hoverBackground = "#E5E7E8";
      activeBackground = "#E5E7E8";
      textActiveColor = "#002035";
      break;
    case "dark":
      background = "#000000";
      hoverBackground = "#333333";
      activeBackground = "#1A1A1A";
      textActiveColor = "#FFFFFF";
      break;
    default:
      break;
  }

  return (
    <Wrapper
      id={props.id}
      width={width}
      height={height}
      borderRadius={borderRadius}
      background={background}
      boxShadow={boxShadow}
      hoverBackground={hoverBackground}
      focusBackground={activeBackground}
      color={textColor}
      activeColor={textActiveColor}
      disabledBackground={disabledBackground}
      disabledColor={textDisabledColor}
      disabled={disabled}
      sx={sx}
      onClick={onClick}>
      <IconWrapper hide={startIconNonExistent}>{startIcon}</IconWrapper>
      <Text fontWeight={fontWeight}>{children}</Text>
      <IconWrapper hide={endIconNonExistent}>{endIcon}</IconWrapper>
    </Wrapper>
  );
};

export default ContainedButton;
